import { AddCircleOutline } from '@mui/icons-material'
import { Box, styled, useTheme } from '@mui/material'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { IEmptyStatusProps } from 'src/generated-types/empty-status'
import { ITimeReport } from 'src/generated-types/time-report'
import { MUIThemeColors } from 'src/mui-theme'
import { Padding } from 'src/styles/paddingComponent'
import { getThemeColor } from 'src/utils'
import { invalidateTimeReportCache, useAddTimeReport } from '../../../api/time-report'
import { permissionValue } from '../../../enums/permissionValue'
import { useDialogCtx } from '../../../hooks/context-hooks'
import TimeReportForm from './TimeReportForm'
import { TimeReportTable } from './TimeReportsTable'
import { diffMinutes, formatHoursMinutes } from './TimeReportCells'

interface TimeReportsContainerProps extends IEmptyStatusProps {
  disabledButton: boolean
  scheduledTaskId?: EntityId
  customerId: EntityId
  height?: number
  rowsToFit?: { min?: number; max?: number; fill?: boolean }
}

export const TimeReportsContainer = ({
  scheduledTaskId,
  disabledButton = false,
  customerId,
  emptyStatusContent,
  emptyStatusTitle,
  emptyStatusContentFontSize,
  emptyStatusTitleFontSize,
  height,
  rowsToFit,
}: TimeReportsContainerProps) => {
  const { t: translate } = useTranslation(['time-reports', 'common'])
  const { showDialog } = useDialogCtx()
  const { mutateAsync } = useAddTimeReport(scheduledTaskId as EntityId)
  const theme = useTheme()
  const [timeReports, setTimeReports] = useState<ITimeReport[]>([])

  const handleItemsChange = useCallback((items: ITimeReport[]) => {
    setTimeReports(prev => {
      if (prev.length === items.length && prev.every((p, i) => p === items[i])) {
        return prev
      }
      return items
    })
  }, [])

  const totalMinutes = timeReports.reduce(
    (sum, report) => sum + diffMinutes(report.fromDateTime, report.toDateTime),
    0,
  )
  const totalTimeString = formatHoursMinutes(totalMinutes)

  const onAddTimeReport = useCallback(() => {
    const previous = timeReports.reduce<ITimeReport | undefined>((latest, r) => {
      if (!r.toDateTime) return latest
      if (!latest) return r
      return new Date(r.toDateTime).getTime() > new Date(latest.toDateTime).getTime() ? r : latest
    }, undefined)

    const prefill = previous
      ? {
          fromDateTime: previous.toDateTime,
          resource: previous.resourceId ? { id: previous.resourceId, name: previous.resourceName } : undefined,
        }
      : undefined

    showDialog(TimeReportForm, {
      title: translate('addTimeReport'),
      opts: {
        maxWidth: 'sm',
        fullWidth: true,
      },
      componentProps: {
        customerId: customerId,
        initialValues: prefill,
        onSubmit: async formValues => {
          formValues.scheduledTaskId = scheduledTaskId
          await mutateAsync(formValues)
          invalidateTimeReportCache.useGetTimeReports(scheduledTaskId as EntityId)
        },
      },
    })
  }, [customerId, mutateAsync, scheduledTaskId, showDialog, translate, timeReports])

  return (
    <>
      <CommonPageLayout
        {...(rowsToFit?.fill ? { flex: 1, minHeight: 0, childrenProps: { flex: 1, minHeight: 0 } } : {})}
        titleSection={{ subtitle: translate('timeReports') }}
        topAction={[
          <TotalTimeBox key="total-time" theme={theme}>
            {translate('totalTime', { ns: 'time-reports' })}: {totalTimeString}
          </TotalTimeBox>,
          {
            buttonText: translate('timeReports'),
            buttonIcon: <AddCircleOutline />,
            onClick: onAddTimeReport,
            disabled: disabledButton,
            permissionBinaryValues: [
              permissionValue.Locality.Task.Update,
              permissionValue.Locality.Task.Write,
              permissionValue.Locality.Task.perform,
            ],
            variant: 'outlined',
            shadowWeight: 0,
          },
        ]}
      >
        <Padding.p8
          borderRadius={'4px'}
          bgcolor={getThemeColor(theme, MUIThemeColors.white)}
          {...(rowsToFit?.fill ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : {})}
        >
          <TimeReportTable
            scheduleTaskId={scheduledTaskId as EntityId}
            disabledButton={disabledButton}
            customerId={customerId}
            onItemsChange={handleItemsChange}
            emptyStatusContent={emptyStatusContent}
            emptyStatusTitle={emptyStatusTitle}
            emptyStatusContentFontSize={emptyStatusContentFontSize}
            emptyStatusTitleFontSize={emptyStatusTitleFontSize}
            height={height}
            rowsToFit={rowsToFit}
          />
        </Padding.p8>
      </CommonPageLayout>
    </>
  )
}

export const TotalTimeBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: getThemeColor(theme, MUIThemeColors.secondaryLight),
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
  borderRadius: '4px',
  fontWeight: 600,
  fontSize: '0.875rem',
  display: 'flex',
  alignItems: 'center',
}))
